import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, X, Plus, GripVertical, Save, Eye } from "lucide-react";
import { ACCESS_OPTIONS } from "@/lib/tiers";
import { ENGINE_OPTIONS, DEV_STATUS_OPTIONS } from "@/lib/engines";
import RequestedByPicker from "@/components/RequestedByPicker";

const DRAFT_KEY = "kr_post_draft";

const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `dl_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const withUid = (dl) => (dl && dl._uid ? dl : { ...dl, _uid: uid() });

function slugify(s) {
  return (s || "").toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/[\s_-]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function AdminPostEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const editing = !!id;
  const [form, setForm] = useState(() => {
    const empty = {
      title: "", slug: "", thumbnail_url: "", screenshots: [], description: "", changelog: "",
      version: "", release_date: "", tags: [], access_level: "public", featured: false,
      status: "draft", engine: "other", dev_status: "completed",
      requested_by_user_id: "", requested_by_user_display: null,
      download_links: [{ _uid: uid(), label: "Main Game", url: "", note: "", file_id: null }],
    };
    if (id) return empty;
    // Lazy-restore draft from sessionStorage for new posts only
    try {
      const draft = sessionStorage.getItem(DRAFT_KEY);
      if (draft) {
        const parsed = JSON.parse(draft);
        parsed.download_links = (parsed.download_links || []).map(withUid);
        return { ...empty, ...parsed };
      }
    } catch (e) {
      console.error("Failed to parse saved draft:", e);
    }
    return empty;
  });
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [autoSlug, setAutoSlug] = useState(true);
  const fileInputThumb = useRef(null);
  const fileInputShots = useRef(null);

  // Anchor + portal-positioning for the tag dropdown so it escapes the
  // .glass-strong card's `backdrop-filter` stacking context (which was
  // clipping it behind the Thumbnail / Screenshots / Download Links cards).
  const tagInputRef = useRef(null);
  const [tagDropdownRect, setTagDropdownRect] = useState(null);

  // -------- Tag autocomplete (server-side, debounced) --------
  // Scales to 10k+ tags by querying /api/tags/search?q=… instead of loading the full library.
  const [tagSuggestions, setTagSuggestions] = useState([]);   // [{name, count}]
  const [tagAcIdx, setTagAcIdx] = useState(-1);
  const [tagSearchLoading, setTagSearchLoading] = useState(false);
  // Track the latest request so out-of-order responses don't clobber the UI.
  const tagSearchSeqRef = useRef(0);

  useEffect(() => {
    const q = (tagInput || "").trim();
    if (!q) { setTagSuggestions([]); return; }
    // 275ms debounce — single in-flight request at a time. The loading flag
    // updates inside the timeout callback (legitimate async pattern).
    const seq = ++tagSearchSeqRef.current;
    const handle = setTimeout(async () => {
      setTagSearchLoading(true);
      try {
        const { data } = await api.get("/tags/search", {
          params: { q, limit: 12, include_drafts: true },
        });
        if (seq !== tagSearchSeqRef.current) return; // stale
        const filtered = (data.tags || []).filter((t) => !form.tags.includes(t.name));
        setTagSuggestions(filtered);
        setTagAcIdx(-1);
      } catch (e) {
        if (seq === tagSearchSeqRef.current) setTagSuggestions([]);
      } finally {
        if (seq === tagSearchSeqRef.current) setTagSearchLoading(false);
      }
    }, 275);
    return () => clearTimeout(handle);
  }, [tagInput, form.tags]);

  // Whether the typed value is NEW — true when no exact match in suggestions and not selected.
  const isNewTag = useMemo(() => {
    const q = (tagInput || "").trim();
    if (!q) return false;
    if (form.tags.includes(q)) return false;
    const ql = q.toLowerCase();
    return !tagSuggestions.some((t) => t.name.toLowerCase() === ql);
  }, [tagInput, tagSuggestions, form.tags]);

  // Keep the portal-rendered dropdown anchored to the tag input on every
  // layout change (scroll, resize, suggestion list grow/shrink).
  const dropdownVisible =
    tagInput.trim() && (tagSuggestions.length > 0 || isNewTag || tagSearchLoading);

  useLayoutEffect(() => {
    if (!dropdownVisible) { setTagDropdownRect(null); return; }
    const update = () => {
      const el = tagInputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setTagDropdownRect({ left: r.left, top: r.bottom + 4, width: r.width });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [dropdownVisible, tagSuggestions.length, isNewTag, tagSearchLoading]);

  // Load post when editing an existing post
  useEffect(() => {
    if (!editing) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/posts");
        const post = (data.posts || []).find((p) => p.id === id);
        if (!cancelled && post) {
          const normalized = { ...post, download_links: (post.download_links || []).map(withUid) };
          setForm((prev) => ({ ...prev, ...normalized }));
          setAutoSlug(false);
        }
      } catch (e) {
        console.error("Failed to load post for editing:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [id, editing]);

  // Autosave drafts of new posts to sessionStorage (cleared on tab close)
  useEffect(() => {
    if (editing) return;
    const t = setTimeout(() => {
      try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(form)); }
      catch (e) { console.error("Draft autosave failed:", e); }
    }, 600);
    return () => clearTimeout(t);
  }, [form, editing]);

  // Update title and (if user hasn't manually edited slug) auto-derive slug from it
  const onTitleChange = (newTitle) => {
    setForm((f) => ({ ...f, title: newTitle, slug: autoSlug ? slugify(newTitle) : f.slug }));
  };

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const onThumbChange = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    try { const url = await upload(f); setForm({ ...form, thumbnail_url: url }); toast.success("Thumbnail uploaded"); }
    catch (err) { console.error("Thumbnail upload failed:", err); toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const onShotsChange = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    try {
      const urls = [];
      for (const f of files) urls.push(await upload(f));
      setForm({ ...form, screenshots: [...form.screenshots, ...urls] });
      toast.success(`${urls.length} screenshot${urls.length > 1 ? "s" : ""} uploaded`);
    } catch (err) { console.error("Screenshots upload failed:", err); toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const reorderShots = (from, to) => {
    const arr = [...form.screenshots]; const [m] = arr.splice(from, 1); arr.splice(to, 0, m);
    setForm({ ...form, screenshots: arr });
  };

  const onTagAdd = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const t = tagInput.trim(); if (!t) return;
      if (!form.tags.includes(t)) setForm({ ...form, tags: [...form.tags, t] });
      setTagInput("");
      setTagAcIdx(-1);
    } else if (e.key === "ArrowDown") {
      if (tagSuggestions.length > 0) { e.preventDefault(); setTagAcIdx((i) => Math.min(i + 1, tagSuggestions.length - 1)); }
    } else if (e.key === "ArrowUp") {
      if (tagSuggestions.length > 0) { e.preventDefault(); setTagAcIdx((i) => Math.max(i - 1, 0)); }
    } else if (e.key === "Tab" && tagAcIdx >= 0 && tagSuggestions[tagAcIdx]) {
      e.preventDefault();
      const pick = tagSuggestions[tagAcIdx].name;
      if (!form.tags.includes(pick)) setForm({ ...form, tags: [...form.tags, pick] });
      setTagInput("");
      setTagAcIdx(-1);
    } else if (e.key === "Escape") {
      setTagAcIdx(-1);
    }
  };

  const pickTagSuggestion = (name) => {
    if (!form.tags.includes(name)) setForm({ ...form, tags: [...form.tags, name] });
    setTagInput("");
    setTagAcIdx(-1);
  };

  const submit = async (status) => {
    setLoading(true);
    try {
      // Strip internal _uid before sending to backend
      const cleanLinks = (form.download_links || [])
        .filter((dl) => dl.label && (dl.url || dl.file_id))
        .map(({ _uid, ...rest }) => rest);
      const payload = { ...form, status, download_links: cleanLinks };
      if (editing) await api.put(`/admin/posts/${id}`, payload);
      else { await api.post("/admin/posts", payload); sessionStorage.removeItem(DRAFT_KEY); }
      toast.success(status === "published" ? "Published" : "Saved as draft");
      nav("/admin/posts");
    } catch (e) {
      console.error("Post save failed:", e);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const uploadGame = async (e, idx) => {
    const f = e.target.files[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/uploads/game", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const arr = [...form.download_links];
      arr[idx] = { ...arr[idx], file_id: data.id, label: arr[idx].label || data.filename, url: arr[idx].url || `(file: ${data.filename})` };
      setForm({ ...form, download_links: arr });
      toast.success("Game file uploaded — protected");
    } catch (err) { console.error("Game file upload failed:", err); toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-xl font-bold text-white">{editing ? "Edit" : "New"} TL Game Post</h2>
        <div className="flex gap-2">
          <Button onClick={() => submit("draft")} variant="outline" disabled={loading} data-testid="save-draft-btn"><Save className="w-4 h-4 mr-2" />Save Draft</Button>
          <Button onClick={() => submit("published")} className="bg-rose-600 hover:bg-rose-500" disabled={loading || !form.title} data-testid="publish-btn"><Eye className="w-4 h-4 mr-2" />Publish</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="glass-strong rounded-xl p-5 space-y-4">
            <div>
              <Label className="text-xs text-zinc-400">Game Title</Label>
              <Input value={form.title} onChange={(e) => onTitleChange(e.target.value)} className="mt-1 bg-black/40 border-white/10" data-testid="post-title-input" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Slug</Label>
              <Input value={form.slug} onChange={(e) => { setAutoSlug(false); setForm({ ...form, slug: slugify(e.target.value) }); }} className="mt-1 bg-black/40 border-white/10 font-mono" data-testid="post-slug-input" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Description (HTML allowed)</Label>
              <Textarea rows={8} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-black/40 border-white/10 font-mono text-xs" data-testid="post-description-input" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Changelog</Label>
              <Textarea rows={5} value={form.changelog} onChange={(e) => setForm({ ...form, changelog: e.target.value })} className="mt-1 bg-black/40 border-white/10 font-mono text-xs" data-testid="post-changelog-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Version</Label>
                <Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} className="mt-1 bg-black/40 border-white/10" data-testid="post-version-input" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Release Date</Label>
                <Input type="date" value={form.release_date || ""} onChange={(e) => setForm({ ...form, release_date: e.target.value })} className="mt-1 bg-black/40 border-white/10" data-testid="post-date-input" />
              </div>
            </div>
            {/* Optional supporter credit — picks a real user (live avatar + role). Leave blank to hide. */}
            <RequestedByPicker
              valueId={form.requested_by_user_id || ""}
              valueDisplay={form.requested_by_user || form.requested_by_user_display || null}
              onChange={(u) => setForm({
                ...form,
                requested_by_user_id: u ? u.id : "",
                requested_by_user_display: u,
              })}
            />
            {/* Legacy plain-text field (read-only fallback if older posts have it). */}
            {form.requested_by && !form.requested_by_user_id && (
              <div className="text-[11px] text-amber-300/80 -mt-2">
                Legacy text credit on this post: <span className="font-semibold">{form.requested_by}</span> — pick a real user above to upgrade.
              </div>
            )}
            <div>
              <Label className="text-xs text-zinc-400">Tags — type to search the library, Enter (or Tab on highlight) to add</Label>
              <div className="relative mt-1">
                <Input
                  ref={tagInputRef}
                  value={tagInput}
                  onChange={(e) => { setTagInput(e.target.value); setTagAcIdx(-1); }}
                  onKeyDown={onTagAdd}
                  className="bg-black/40 border-white/10"
                  placeholder="e.g. Visual Novel, RPG, Dark Fantasy…"
                  data-testid="post-tag-input"
                  autoComplete="off"
                />
                {dropdownVisible && tagDropdownRect && createPortal(
                  <div
                    className="fixed z-[9000] max-h-72 overflow-y-auto rounded-md border border-white/10 bg-zinc-950/98 backdrop-blur shadow-2xl ring-1 ring-rose-900/30"
                    style={{ left: tagDropdownRect.left, top: tagDropdownRect.top, width: tagDropdownRect.width }}
                    data-testid="tag-autocomplete"
                    role="listbox"
                  >
                    {tagSearchLoading && tagSuggestions.length === 0 && (
                      <div className="px-3 py-2 text-xs text-zinc-500 italic">Searching tag library…</div>
                    )}
                    {tagSuggestions.map((t, idx) => {
                      // Render a subtle divider when transitioning from prefix → substring matches,
                      // so reviewers visually understand why a fuzzy-looking tag appears in the list.
                      const prev = idx > 0 ? tagSuggestions[idx - 1] : null;
                      const isFirstSubstring = t.match_type === "substring" && (!prev || prev.match_type !== "substring");
                      return (
                        <React.Fragment key={t.name}>
                          {isFirstSubstring && (
                            <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-zinc-500 bg-black/60 border-t border-white/5">
                              Contains &quot;{tagInput.trim()}&quot;
                            </div>
                          )}
                          <button
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); pickTagSuggestion(t.name); }}
                            onMouseEnter={() => setTagAcIdx(idx)}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ${idx === tagAcIdx ? "bg-rose-600/20 text-white" : "text-zinc-200 hover:bg-white/5"}`}
                            data-testid={`tag-suggest-${t.name}`}
                          >
                            <span className="truncate">{t.name}</span>
                            <span className="text-[10px] text-zinc-400 bg-black/40 px-1.5 py-0.5 rounded-full flex-shrink-0">{t.count}</span>
                          </button>
                        </React.Fragment>
                      );
                    })}
                    {isNewTag && (
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const t = tagInput.trim();
                          if (t && !form.tags.includes(t)) setForm({ ...form, tags: [...form.tags, t] });
                          setTagInput("");
                          setTagAcIdx(-1);
                        }}
                        className="w-full text-left px-3 py-2 text-sm border-t border-white/10 bg-emerald-900/10 text-emerald-200 hover:bg-emerald-900/25"
                        data-testid="tag-create-new"
                      >
                        <span className="text-[10px] uppercase tracking-wider bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded mr-2">new</span>
                        Create tag: <b>{tagInput.trim()}</b>
                      </button>
                    )}
                  </div>,
                  document.body,
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {form.tags.map((t) => (
                  <span key={t} className="px-2 py-1 rounded text-xs bg-rose-900/30 border border-rose-500/30 text-rose-200 flex items-center gap-1">
                    {t}
                    <button onClick={() => setForm({ ...form, tags: form.tags.filter((x) => x !== t) })} className="text-rose-300 hover:text-white"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Thumbnail */}
          <div className="glass-strong rounded-xl p-5">
            <Label className="text-xs text-zinc-400">Thumbnail (single)</Label>
            <input type="file" ref={fileInputThumb} accept="image/*" hidden onChange={onThumbChange} data-testid="thumb-file-input" />
            <div className="mt-2 flex items-center gap-4">
              {form.thumbnail_url && <img src={fileUrl(form.thumbnail_url)} alt="" className="w-40 h-24 object-cover rounded" />}
              <Button variant="outline" onClick={() => fileInputThumb.current?.click()} data-testid="upload-thumb-btn"><Upload className="w-4 h-4 mr-2" />{form.thumbnail_url ? "Replace" : "Upload"} Thumbnail</Button>
              {form.thumbnail_url && <Button variant="outline" onClick={() => setForm({ ...form, thumbnail_url: "" })} className="border-rose-500/40 text-rose-300" data-testid="remove-thumb-btn"><X className="w-4 h-4" /></Button>}
            </div>
          </div>

          {/* Screenshots */}
          <div className="glass-strong rounded-xl p-5">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-zinc-400">Screenshots (drag to reorder)</Label>
              <input type="file" ref={fileInputShots} accept="image/*" multiple hidden onChange={onShotsChange} data-testid="shots-file-input" />
              <Button variant="outline" size="sm" onClick={() => fileInputShots.current?.click()} data-testid="upload-shots-btn"><Upload className="w-4 h-4 mr-2" />Add</Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
              {form.screenshots.map((s, i) => (
                <div
                  key={s}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData("idx", String(i))}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => reorderShots(parseInt(e.dataTransfer.getData("idx")), i)}
                  className="relative group rounded-md overflow-hidden border border-white/10"
                  data-testid={`shot-${i}`}
                >
                  <img src={fileUrl(s)} alt="" className="w-full h-24 object-cover" />
                  <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                    <button onClick={() => setForm({ ...form, screenshots: form.screenshots.filter((_, j) => j !== i) })} className="bg-black/70 p-1 rounded">
                      <X className="w-3 h-3 text-rose-300" />
                    </button>
                  </div>
                  <div className="absolute top-1 left-1 bg-black/70 p-0.5 rounded"><GripVertical className="w-3 h-3 text-zinc-400" /></div>
                </div>
              ))}
            </div>
            {form.screenshots.length === 0 && <div className="text-xs text-zinc-500 mt-3">No screenshots yet.</div>}
          </div>

          {/* Download links */}
          <div className="glass-strong rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs text-zinc-400">Download Links (multiple)</Label>
              <Button variant="outline" size="sm" onClick={() => setForm({ ...form, download_links: [...form.download_links, { _uid: uid(), label: "", url: "", note: "", file_id: null }] })} data-testid="add-download-btn"><Plus className="w-3 h-3 mr-1" />Add</Button>
            </div>
            <div className="space-y-3">
              {form.download_links.map((dl, i) => (
                <div key={dl._uid || `dl-${i}`} className="p-3 rounded-lg bg-black/30 border border-white/10 grid grid-cols-1 md:grid-cols-12 gap-2" data-testid={`download-row-${i}`}>
                  <Input placeholder="Label (e.g. Main Game)" value={dl.label} onChange={(e) => { const arr = [...form.download_links]; arr[i] = { ...dl, label: e.target.value }; setForm({ ...form, download_links: arr }); }} className="md:col-span-3 bg-black/40 border-white/10" />
                  <Input placeholder="URL or paste" value={dl.url} onChange={(e) => { const arr = [...form.download_links]; arr[i] = { ...dl, url: e.target.value }; setForm({ ...form, download_links: arr }); }} className="md:col-span-5 bg-black/40 border-white/10" />
                  <Input placeholder="Note (optional)" value={dl.note} onChange={(e) => { const arr = [...form.download_links]; arr[i] = { ...dl, note: e.target.value }; setForm({ ...form, download_links: arr }); }} className="md:col-span-3 bg-black/40 border-white/10" />
                  <label className="md:col-span-1 flex items-center justify-center cursor-pointer rounded bg-white/5 hover:bg-white/10 border border-white/10">
                    <input type="file" hidden onChange={(e) => uploadGame(e, i)} data-testid={`upload-game-${i}`} />
                    <Upload className="w-4 h-4" />
                  </label>
                  {dl.file_id && <div className="md:col-span-12 text-[10px] text-emerald-400">Linked to protected file ({dl.file_id.slice(0, 8)}…)</div>}
                  <button onClick={() => setForm({ ...form, download_links: form.download_links.filter((_, j) => j !== i) })} className="md:col-span-12 text-rose-300 text-xs mt-1 text-right hover:underline" data-testid={`remove-download-${i}`}>Remove</button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="space-y-5">
          <div className="glass-strong rounded-xl p-5 space-y-4">
            <div>
              <Label className="text-xs text-zinc-400">Access Level</Label>
              <Select value={form.access_level} onValueChange={(v) => setForm({ ...form, access_level: v })}>
                <SelectTrigger className="mt-1 bg-black/40 border-white/10" data-testid="post-access-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-white/10 text-white">
                  {ACCESS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Engine <span className="text-rose-400">*</span></Label>
              <Select value={form.engine || "other"} onValueChange={(v) => setForm({ ...form, engine: v })}>
                <SelectTrigger className="mt-1 bg-black/40 border-white/10" data-testid="post-engine-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-white/10 text-white">
                  {ENGINE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`engine-opt-${o.value}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Status <span className="text-rose-400">*</span></Label>
              <Select value={form.dev_status || "completed"} onValueChange={(v) => setForm({ ...form, dev_status: v })}>
                <SelectTrigger className="mt-1 bg-black/40 border-white/10" data-testid="post-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-white/10 text-white">
                  {DEV_STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`status-opt-${o.value}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm text-zinc-200">Featured on Homepage</Label>
              <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="post-featured-switch" />
            </div>
            <div className="text-xs text-zinc-500">
              Status: <span className={form.status === "published" ? "text-emerald-400" : "text-amber-400"}>{form.status}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
